import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pencil, Eye } from 'lucide-react';
import { formatDay } from '@/pages/coordinator/AssignMentorDialog';

export const OFFER_STATUS = {
  draft: { label: 'Draft', cls: 'bg-amber-50 text-amber-700' },
  awaiting_company: { label: 'Awaiting your confirmation', cls: 'bg-[#FFF0ED] text-[#F06651]' },
  submitted: { label: 'With the school', cls: 'bg-sky-50 text-sky-700' },
  changes_requested: { label: 'Changes requested', cls: 'bg-amber-50 text-amber-700' },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-600' },
  rejected: { label: 'Not accepted', cls: 'bg-gray-100 text-gray-500' },
};

export const PROGRAM_STATUS = {
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-500' },
  open: { label: 'Open', cls: 'bg-emerald-50 text-emerald-600' },
  closed: { label: 'Closed', cls: 'bg-gray-100 text-gray-500' },
};

/** Offers needing the company to act, in the order they should be dealt with. */
export const OPEN_OFFER_STATES = ['awaiting_company', 'changes_requested', 'draft'];

export const Stat = ({ label, value, testId, to }) => {
  const body = (
    <>
      <p className="text-2xl font-bold text-[#1F2937] font-['Outfit'] leading-none">{value}</p>
      <p className="text-[11px] text-[#6B7280] mt-1.5 leading-tight">{label}</p>
    </>
  );
  if (!to) {
    return <div className="bg-white rounded-2xl border border-[#E5E7EB] px-4 py-3.5" data-testid={testId}>{body}</div>;
  }
  return (
    <Link to={to} aria-label={`${label}: ${value}`}
      className="bg-white rounded-2xl border border-[#E5E7EB] px-4 py-3.5 block transition-all duration-200 hover:border-[#F97D6A]/60 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F97D6A] focus-visible:ring-offset-2"
      data-testid={testId}>
      {body}
    </Link>
  );
};

export function PageHeader({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-[#6B7280] mt-1.5">{subtitle}</p>}
    </div>
  );
}

export function OfferRow({ offer: o, hideProgram = false, readOnly = false }) {
  const st = OFFER_STATUS[o.status] || { label: o.status, cls: 'bg-gray-100 text-gray-500' };
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] px-4 py-3" data-testid={`offer-row-${o.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-[#1F2937]">{o.title || 'Untitled offer'}</p>
            <Badge className={`border-0 text-[10px] ${st.cls}`} data-testid={`offer-status-${o.id}`}>{st.label}</Badge>
            {o.review_state === 'pending_reapproval' && (
              <Badge className="border-0 text-[10px] bg-sky-50 text-sky-700" data-testid={`offer-reapproval-${o.id}`}>Changes with the school</Badge>
            )}
            {o.referred_by_parent_name && (
              <Badge className="border-0 text-[10px] bg-[#F3F4F6] text-[#6B7280]">Referred by {o.referred_by_parent_name}</Badge>
            )}
            {o.career_mapping_status === 'pending' && (
              <Badge className="border-0 text-[10px] bg-amber-50 text-amber-700" data-testid={`offer-career-pending-${o.id}`}>Pending career review</Badge>
            )}
            {o.career_mapping_status === 'rejected' && (
              <Badge className="border-0 text-[10px] bg-red-50 text-red-600" data-testid={`offer-career-rejected-${o.id}`}>Career not approved</Badge>
            )}
          </div>
          {o.career_name && (
            <p className="text-[11px] text-[#9CA3AF] mt-1" data-testid={`offer-career-${o.id}`}>Career: {o.career_name}</p>
          )}
          <p className="text-[11px] text-[#6B7280] mt-1">
            {hideProgram ? '' : `${o.school_name} · ${o.program_name}`}
            {o.start_date ? `${hideProgram ? '' : ' · '}${formatDay(o.start_date)} – ${formatDay(o.end_date)}` : ''}
            {o.positions ? ` · ${o.positions} place${o.positions === 1 ? '' : 's'}` : ''}
          </p>
          {o.coordinator_note && (
            <p className="text-[11px] text-amber-700 mt-1 leading-relaxed" data-testid={`offer-note-${o.id}`}>
              School note: “{o.coordinator_note}”
            </p>
          )}
        </div>
        <Link to={`/partner/offers/${o.id}`} className="shrink-0">
          <Button size="sm" variant="outline" className="rounded-lg text-xs border-gray-200" data-testid={`open-offer-${o.id}`}>
            {readOnly ? <Eye className="h-3.5 w-3.5 mr-1" /> : <Pencil className="h-3.5 w-3.5 mr-1" />}
            {readOnly ? 'View record'
              : o.status === 'awaiting_company' ? 'Review & confirm' : o.status === 'approved' ? 'View' : 'Continue'}
          </Button>
        </Link>
      </div>
    </div>
  );
}
